import Icon from '@vfz/ui/dist/components/icon/icon';
import { DisableBodyScroll } from 'components/DisableBodyScroll';
import { Loader } from 'components/Loader';
import { appSettings } from 'config/app.settings';
import { useClickOutsideAndKeyNavigation } from 'hooks/use-click-outside-and-key-navigation.hook';
import { useIframeMessageRef } from 'hooks/use-iframe-message.hook';
import { useWindowWidth } from 'hooks/use-window-width.hook';
import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { breakpointDesktop } from 'theme';
import { ContentUtil } from 'utils/content';

import { content } from './conversational-ai.content';
import * as S from './conversational-ai.styles';

const contentUtil = new ContentUtil({ content, componentName: 'Conversational AI' });

const BASE_URL = appSettings.ENV_CONVERSATIONAL_AI_URL || 'https://zgo-dev.vfz-services.nl/conversational-ai';
const MESSAGE_TYPE_CLOSE = 'ca-chat:close';
const MESSAGE_TYPE_FOCUS = 'ca-chat:focus';
const MESSAGE_TYPE_MINIMIZE = 'ca-chat:minimize';
const MESSAGE_PREFIX_CONVERSATION_ID = 'ca-chat:conversation-id:';

export type ConversationalAIPortalProps = {
  opened: boolean;
  src: string;
  iframeId: string;
  close: () => void;
  minimize: () => void;
  open: () => void;
  onNewConversationId: (id: string) => void;
};

export const ConversationalAIPortal = ({
  opened,
  src,
  iframeId,
  close,
  minimize,
  open,
  onNewConversationId,
}: ConversationalAIPortalProps) => {
  const windowWidth = useWindowWidth();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [conversationalAIRoot, setConversationalAIRoot] = useState<HTMLElement | null>(null);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const fullWidth = windowWidth < breakpointDesktop;

  const handleClickOutside = useCallback(() => {
    if (opened) minimize();
  }, [opened, minimize]);

  const portalRef = useClickOutsideAndKeyNavigation(handleClickOutside, true) as RefObject<HTMLDivElement>;

  useEffect(() => {
    setConversationalAIRoot(document.getElementById('conversational-ai-root'));
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (opened && iframeRef.current) {
        sendFocusPostMessage();
      }
    });
  }, [iframeRef, opened]);

  const sendFocusPostMessage = () => {
    iframeRef.current?.contentWindow?.postMessage({ type: MESSAGE_TYPE_FOCUS }, BASE_URL);
  };

  const handleIframeLoad = () => {
    setIsIframeLoading(false);
    sendFocusPostMessage();
  };

  useIframeMessageRef(iframeRef, (data: object | string) => {
    if (typeof data !== 'string') return;

    if (data === MESSAGE_TYPE_MINIMIZE) {
      minimize();
    } else if (data === MESSAGE_TYPE_CLOSE) {
      close();
    } else if (data.startsWith(MESSAGE_PREFIX_CONVERSATION_ID)) {
      const conversationId = data.slice(MESSAGE_PREFIX_CONVERSATION_ID.length);
      if (conversationId) {
        onNewConversationId(conversationId);
      }
    }
  });

  if (!conversationalAIRoot || !BASE_URL) {
    if (!BASE_URL) {
      console.warn('ConversationalAI: ENV_CONVERSATIONAL_AI_URL is not configured');
    }
    return null;
  }

  return createPortal(
    <S.Portal
      ref={portalRef}
      fullWidth={fullWidth}
      role={opened ? 'dialog' : undefined}
      aria-modal={opened ? true : undefined}>
      {!opened && (
        <S.ResumeButton onClick={open} type="button" aria-label={contentUtil.translateToString('submitAriaLabel')}>
          <Icon name="message" />
        </S.ResumeButton>
      )}
      {opened && isIframeLoading && (
        <S.LoadingOverlay>
          <Loader delay={200} />
        </S.LoadingOverlay>
      )}
      <S.Iframe
        visible={opened}
        ref={iframeRef}
        src={src}
        id={iframeId}
        title={contentUtil.translateToString('title')}
        onLoad={handleIframeLoad}
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-top-navigation-by-user-activation"
        referrerPolicy="no-referrer"
        loading="eager"
      />
      {opened && fullWidth && <DisableBodyScroll />}
    </S.Portal>,
    conversationalAIRoot
  );
};
